import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronRight } from 'lucide-react';
import { getServices } from '../lib/api';
import { useLanguage } from '../lib/LanguageContext';

// Icon mapping for services
const iconMap = {
  'bi-building':   '🏗️',
  'bi-bricks':     '🧱',
  'bi-paint-bucket': '🎨',
  'bi-grid-3x3':   '🔲',
  'bi-droplet':    '🏠', 
  'bi-trash3':     '🗑️',
  'bi-hammer':     '🔨',
  'bi-tools':      '🔧',
};

export default function ServicesPage() {
  const [services, setServices] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const revealRefs = useRef([]);
  const { t, language } = useLanguage();

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const res = await getServices();
        setServices(res.data);
      } catch (err) {
        console.error('Error fetching services:', err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchServices();
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );

    revealRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, [services]);

  const addToRefs = (el) => {
    if (el && !revealRefs.current.includes(el)) {
      revealRefs.current.push(el);
    }
  };

  // Helper function to get localized service field
  const getLocalizedField = (service, field) => {
    const enField = `${field}_en`;
    const deField = `${field}_de`;
    if (language === 'en' && service[enField]) {
      return service[enField];
    }
    return service[deField] || service[field] || '';
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div data-testid="services-page">
      {/* Hero Section */}
      <section className="relative py-24 lg:py-32 overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img 
            src="/hero-img.jpg" 
            alt="Bauarbeiten"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-[#0A0A0A]/80" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 text-center">
          <span className="text-[#D4B982] text-sm font-medium uppercase tracking-widest">{t('servicesPage.subtitle')}</span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mt-4 mb-6">
            {t('servicesPage.title1')} <span className="text-[#D4B982]">{t('servicesPage.title2')}</span>
          </h1>
          <p className="text-white/70 text-lg max-w-2xl mx-auto">
            {t('servicesPage.description')}
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-24 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div
                key={service.id}
                ref={addToRefs}
                className={`reveal stagger-${(index % 6) + 1}`}
              >
                <Link
                  to={`/leistungen/${service.slug}`}
                  className="group block bg-white border border-[#E4E4E7] hover:border-[#D4B982] transition-all duration-500 card-hover h-full overflow-hidden"
                  data-testid={`service-card-${service.slug}`}
                >
                  {service.image_url && (
                    <div className="relative h-56 overflow-hidden">
                      <img 
                        src={service.image_url} 
                        alt={getLocalizedField(service, 'title')}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A]/70 via-transparent to-transparent" />
                      <div className="absolute bottom-4 left-4 right-4">
                        <span className="inline-flex items-center gap-2 bg-[#D4B982] text-[#0A0A0A] px-3 py-1 text-xs font-semibold uppercase tracking-wider">
                          {iconMap[service.icon] || '🔧'} Service
                        </span>
                      </div>
                    </div>
                  )}
                  <div className="p-6">
                    <h3 className="text-xl font-semibold text-[#0A0A0A] mb-3 group-hover:text-[#D4B982] transition-colors">
                      {getLocalizedField(service, 'title')}
                    </h3>
                    <p className="text-[#71717A] mb-4 line-clamp-3">
                      {getLocalizedField(service, 'short')}
                    </p>
                    <span className="inline-flex items-center gap-2 text-sm font-medium text-[#D4B982] group-hover:gap-3 transition-all">
                      {t('services.viewDetails')}
                      <ArrowRight size={16} />
                    </span>
                  </div>
                </Link>
              </div>
            ))}
          </div>

          {services.length === 0 && (
            <div className="text-center py-12">
              <p className="text-[#71717A]">{language === 'en' ? 'No services available.' : 'Keine Leistungen verfügbar.'}</p>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-[#F4F4F5]">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-[#0A0A0A] mb-4">
            {t('servicesPage.ctaTitle')}
          </h2>
          <p className="text-[#71717A] mb-6 max-w-xl mx-auto">
            {t('servicesPage.ctaDescription')}
          </p>
          <Link
            to="/kontakt"
            className="inline-flex items-center gap-2 bg-[#D4B982] text-[#0A0A0A] px-8 py-4 text-sm font-semibold uppercase tracking-wider hover:bg-[#C5A059] transition-colors btn-ripple"
            data-testid="services-cta"
          >
            {t('servicesPage.ctaButton')}
            <ChevronRight size={18} />
          </Link>
        </div>
      </section>
    </div>
  );
}
